import { OUTREACH_STATUS_ENUM } from '../../utils/rpmPatientFields';
import Strings from '../../Strings';
import { getBatteryStatus, signalRender } from '../../utils';
import { isAccessProgram, programs, programsAndSubprograms } from '../../utils/cmsPrograms';
import { DeviceClass, DeviceTypes } from '../../utils/deviceTypes';
import { supportedLanguages } from '../../utils/language';
import { KPIS } from '../../utils/readings';
import { hasPermission, ORG_FEATURES, orgFeatureValue } from '../../utils/userPermissions';
import { isSuperUser } from '../../utils/userRoles';
import { OrderStatusEnum } from '../../pages/Shipping/ShippingOrders';
import { RPM_PATIENT_STATUS } from '../../constants';
import { getOrganizationHierarchyRows } from '../../pages/Organizations/organizationHierarchy';

export const VALUE_FOR_NO_FILTER = "no_filter";

const normalizeSupportedProgram = program => {
  const normalized = String(program ?? '').trim().toLowerCase();

  if (['rtmmusculoskeletal', 'rtmrespiratory', 'rtmbehavioralhealth', 'rtm'].includes(normalized)) {
    return programs.RTM;
  }

  if (['ccmbasic', 'ccmcomplex', 'ccm'].includes(normalized)) {
    return programs.CCM;
  }

  return normalized;
};

const getPatientProgramGroupName = program => {
  if (isAccessProgram(program)) {
    return Strings.accessPrograms;
  }
  return program === 'CTAM' ? Strings.clinicalTrials : Strings.careManagementPrograms;
};

const statusFilterBasic = {
  group: 'Status', // interal name
  text: Strings.status, // text to display
  values: [
    {
      text: Strings.all,
      value: 'all',
    },
    {
      text: Strings.new, // text to display
      value: 'New', // internal value
    },
    {
      text: Strings.online,
      value: 'Online',
    },
    {
      text: Strings.offline,
      value: 'Offline',
    },
    {
      text: Strings.active,
      value: 'Active',
    },
  ],
};
const statusFilterExtended = {
  group: 'Status', // interal name
  text: Strings.status, // text to display
  values: [
    {
      text: Strings.all,
      value: 'all',
    },
    {
      text: Strings.new, // text to display
      value: 'New', // internal value
    },
    {
      text: Strings.online,
      value: 'Online',
    },
    {
      text: Strings.offline,
      value: 'Offline',
    },
    {
      text: Strings.active,
      value: 'Active',
    },
    {
      text: Strings.blocked,
      value: 'Blocked',
    },
    {
      text: Strings.suspended,
      value: 'Suspended',
    },
    {
      text: Strings.unassigned,
      value: 'Unassigned',
    },
    {
      text: Strings.manufactured,
      value: 'Manufactured',
    },
    {
      text: 'RMA',
      value: 'RMA',
    }
  ],
};

const getStatusFilter = () => {
  return isSuperUser() ? statusFilterExtended : statusFilterBasic;
};

const getDeviceTypeFilter = (includeHub = false) => {
  const f = Object.values(DeviceTypes)
    .filter(type => ![DeviceClass.kit, DeviceClass.hub].includes(type.deviceClass) || (includeHub && type.deviceClass === DeviceClass.hub))
    .filter(type => hasPermission(type.permission) || isSuperUser())
    .map(type => ({
      text: type.displayName,
      value: type.id,
    })
  );
  f.sort((a, b) => a.text.localeCompare(b.text));
  if (f.length > 1) {
    f.unshift({ text: Strings.all, value: 'all' });
  }
  return {
    group: 'DeviceType',
    text: Strings.deviceType,
    values: f,
  };
}
export const getFiltersDefinition = ({ includeHub = false } = {}) => {
  const supportedPrograms = Array.from(new Set(
    (orgFeatureValue(ORG_FEATURES.supported_subprograms) || [])
      .map(normalizeSupportedProgram)
      .filter(Boolean)
  ));
  return {
    billingStatus: {
      group: 'billingStatus', // interal name
      text: Strings.status, // text to display
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.billingStatus.submitted, // text to display
          value: 'submitted', // internal value
        },
        {
          text: Strings.billingStatus.onhold,
          value: 'onhold',
        },
        {
          text: Strings.billingStatus.pending,
          value: 'pending',
        },
      ],
    },

    Status: getStatusFilter(),
    Sample: {
      group: 'Sample',
      text: Strings.deviceInvoices.sample,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.yes,
          value: 'yes',
        },
        {
          text: Strings.no,
          value: 'no',
        },
      ],
    },

    ReadingStatus: {
      group: 'ReadingStatus',
      text: Strings.status,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.normal,
          value: 'Normal',
        },
        // {
        //   text: Strings.abnormal,
        //   value: 'Abnormal',
        // },
        {
          text: Strings.alert,
          value: 'Alert',
        },
      ],
    },
    Reminder: {
      group: 'Reminder',
      text: Strings.reminder,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: `${Strings.on}`,
          value: 'On',
        },
        {
          text: `${Strings.off}`,
          value: 'Off',
        },
      ],
    },
    Signal: {
      group: 'Signal',
      text: Strings.signal,
      itemRender: e => (e.value !== 'all' ? signalRender({ signal_overall: e.value }) : null),
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: '',
          value: 'Excellent',
        },
        {
          text: '',
          value: 'Good',
        },
        {
          text: '',
          value: 'Medium',
        },
        {
          text: '',
          value: 'Weak',
        },
        {
          text: '',
          value: 'Not available',
        },
      ],
    },
    Faults: {
      group: 'Faults',
      text: Strings.faults,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.none,
          value: 'no faults',
        },
        {
          text: 'Connectivity',
          value: 'Connectivity',
        },
      ],
    },
    Battery: {
      group: 'Battery',
      text: Strings.battery.battery,
      itemRender: e => (e.value === 'all' ? null : e.value === 'Null' ? null : getBatteryStatus(e.value)),
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: '',
          value: 'Good',
        },
        {
          text: '',
          value: 'Medium',
        },
        {
          text: '',
          value: 'Poor',
        },
        {
          text: '-',
          value: 'Null',
        },
      ],
    },
    ActionStatus: {
      group: 'ActionStatus',
      text: Strings.actionStatus,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.open,
          value: 'Open',
        },
        {
          text: Strings.completed,
          value: 'Completed',
        },
      ],
    },
    LogItemType: {
      group: 'LogItemType',
      text: Strings.logitemType,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.note,
          value: 'Note',
        },
        {
          text: Strings.action,
          value: 'Action',
        },
      ],
    },

    usage_state: {
      group: 'usage_state',
      text: Strings.usageState,
      propertyForApi: 'usage_state',
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: 'Created',
          value: 'Created',
        },
        {
          text: 'Assigned',
          value: 'Assigned',
        },
        {
          text: 'Active',
          value: 'Active',
        },
        {
          text: 'Inactive',
          value: 'Inactive',
        },
        {
          text: 'Archived',
          value: 'Archived',
        },
      ],
    },
    administrative_state: {
      group: 'administrative_state',
      text: Strings.administrativeState,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: 'Normal',
          value: 'Normal',
        },
        {
          text: 'Blocked',
          value: 'Blocked',
        },
        {
          text: 'RMA',
          value: 'RMA',
        },
        {
          text: 'Suspended',
          value: 'Suspended',
        },
        {
          text: 'Deleted',
          value: 'Deleted',
        },
      ],
    },
    operational_state: {
      group: 'operational_state',
      text: Strings.operationalState,
      propertyForApi: 'operational_state',
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: 'Functional',
          value: 'Functional',
        },
        {
          text: 'Degraded',
          value: 'Degraded',
        },
        {
          text: 'Disabled',
          value: 'Disabled',
        },
      ],
    },

    Gender: {
      group: 'Gender',
      text: Strings.gender,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.genders.male,
          value: 'male',
        },
        {
          text: Strings.genders.female,
          value: 'female',
        },
        {
          text: Strings.genders.other,
          value: 'other',
        },
      ],
    },

    DeviceType: getDeviceTypeFilter(includeHub),

    PrefabMessageType: {
      group: 'PrefabMessageType',
      text: Strings.type,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.messages.shared,
          value: 'shared_with_org',
        },
        {
          text: Strings.messages.private,
          value: 'private',
        },
      ],
    },

    DeviceStatus: {
      group: 'DeviceStatus',
      text: Strings.status,
      values: [
        {
          text: Strings.deleted,
          value: 'deleted',
        },
      ],
    },
    NoReadingsSince: {
      group: 'NoReadingsSince',
      text: Strings.noReadingsSince,
      type: 'number',
      unit: Strings.days,
      max: 366,
    },
    NewNotesSince: {
      group: 'NewNotesSince',
      text: Strings.newNotesSince,
      type: 'number',
      unit: Strings.days,
      max: 366,
    },

    org_business_state: {
      group: 'org_business_state',
      text: Strings.businessState,
      propertyForApi: 'business_state',
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: 'Created',
          value: 'Created',
        },
        {
          text: 'Proof-of-Service',
          value: 'ProofOfService',
        },
        {
          text: 'Launched',
          value: 'Launched',
        },
        {
          text: 'Disengaged',
          value: 'Disengaged',
        },
      ],
    },
    org_administrative_state: {
      group: 'org_administrative_state',
      text: Strings.administrativeState,
      propertyForApi: 'administrative_state',
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: 'New',
          value: 'New',
        },
        {
          text: 'Trial',
          value: 'Trial',
        },
        {
          text: 'Customer',
          value: 'Customer',
        },
        {
          text: 'Demo',
          value: 'Demo',
        },
        {
          text: 'Engineering/Test',
          value: 'EngineeringTest',
        },
        {
          text: 'Suspended',
          value: 'Suspended',
        },
        {
          text: 'Deleted',
          value: 'Deleted',
        },
      ],
    },
    org_operational_state: {
      group: 'org_operational_state',
      text: Strings.operationalState,
      propertyForApi: 'operational_state',
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: 'Dormant',
          value: 'Dormant',
        },
        {
          text: 'Active',
          value: 'Active',
        },
        {
          text: 'Kit Activity',
          value: 'KitsOnlyActive',
        },
        {
          text: 'In-Use',
          value: 'In-Use',
        },
        {
          text: 'Revenue Active',
          value: 'RevenueActive',
        },
        {
          text: 'Inactive',
          value: 'Inactive',
        },
      ],
    },
    consentForProvider: {
      group: 'consentForProvider',
      text: Strings.status,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.outreachStatusEnum.pending,
          value: 'pending',
        },
        {
          text: Strings.outreachStatusEnum.set,
          value: 'set',
        },
        {
          text: Strings.outreachStatusEnum.retry,
          value: 'retry',
        },
        {
          text: Strings.outreachStatusEnum.not_eligible,
          value: 'not_eligible',
        },
        {
          text: Strings.outreachStatusEnum.declined,
          value: 'declined',
        },
      ],
    },
    outreachStatus: {
      group: 'outreachStatus',
      text: Strings.status,
      values: [{ text: Strings.all, value: 'all' },].concat(Object.values(OUTREACH_STATUS_ENUM).map(s => ({ text: s.label, value: s.value }))),
    },
    program: {
      group: 'program',
      text: Strings.program,
      type: 'radio',
      values: [{ text: Strings.noFilter, value: VALUE_FOR_NO_FILTER }]
        .concat(Object.keys(programs).filter(p => supportedPrograms.includes(programs[p])).map(p => ({ text: Strings.programs[programs[p]], value: p }))),
    },
    programCheckbox: {
      group: 'programCheckbox',
      text: Strings.program,
      values: [{ text: Strings.all, value: 'all' }]
        .concat(Object.keys(programs).filter(p => supportedPrograms.includes(programs[p])).map(p => ({ text: Strings.programs[programs[p]], value: p }))),
    },
    patientProgramCheckbox: {
      group: 'programCheckbox',
      text: Strings.program,
      values: [{ text: Strings.all, value: 'all' }]
        .concat(Object.keys(programs)
        .filter(p => supportedPrograms.includes(programs[p]))
        .map(p => ({
          text: Strings.programs[programs[p]],
          value: p,
          groupedOptionName: getPatientProgramGroupName(p),
        }))),
    },
    patientEnrollmentStatus: {
      group: 'patientEnrollmentStatus',
      text: Strings.enrollmentStatus,
      values: [{ text: Strings.all, value: 'all' }]
        .concat(Object.keys(RPM_PATIENT_STATUS).map(status => ({
          text: Strings.capPatient.patientStatus[status],
          value: status,
        }))),
    },
    programAndSubprogram: {
      group: 'programAndSubprogram',
      text: Strings.program,
      type: 'radio',
      values: [{ text: Strings.noFilter, value: VALUE_FOR_NO_FILTER }]
        .concat(programsAndSubprograms.map(p => ({ text: p.displayText, value: p.value, type: p.type })))
    },
    shipmentStatus: {
      group: 'shipmentStatus',
      text: Strings.shipping.shippingStatus,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
        {
          text: Strings.shipping.shipmentStatuses.new,
          value: 'new',
        },
        {
          text: Strings.shipping.shipmentStatuses.in_transit,
          value: 'in_transit',
        },
        {
          text: Strings.shipping.shipmentStatuses.delivered,
          value: 'delivered',
        },
        {
          text: Strings.shipping.shipmentStatuses.cancelled,
          value: 'cancelled',
        },
      ],
    },
    language: {
      group: 'language',
      text: Strings.language,
      values: [{ text: Strings.all, value: 'all' }]
        .concat(supportedLanguages.map(l => ({ text: Strings.languagesEnum[l.id], value: l.id }))),
    },
    remindersType: {
      group: 'remindersType',
      text: Strings.reminder,
      values: [{ text: Strings.all, value: 'all' }]
        .concat(Object.keys(Strings.defaultRemindersEnum).map(key => ({ text: Strings.defaultRemindersEnum[key], value: key })))
        .concat([{ text: Strings.capPatient.adherenceMilestones, value: 'adherence_milestones' }]),
    },
    phiSafe: {
      group: 'phiSafe',
      text: Strings.isPhiSafe,
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.yes, value: 'true' },
        { text: Strings.no, value: 'false' },
      ],
    },
    alertsTypes: {
      group: 'alertsTypes',
      text: Strings.alerts,
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.capPatient.alertTypes.enrollment, value: 'enrollment' },
        { text: Strings.capPatient.alertTypes.vitals, value: 'measurements' },
        { text: Strings.capPatient.alertTypes.medications, value: 'schedule_missed_doses' },
      ]
    },
    alertsTypesAndOpenActions: {
      group: 'alertsTypesAndOpenActions',
      text: Strings.alerts,
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.capPatient.alertTypes.enrollment, value: 'enrollment' },
        { text: Strings.capPatient.alertTypes.vitals, value: 'vitals' },
        { text: Strings.capPatient.alertTypes.medications, value: 'medications' },
        { text: Strings.capPatient.alertTypes.openActions, value: 'open_actions' },
      ]
    },
    billingAlerts: {
      group: 'billingAlerts',
      text: Strings.capPatient.alertTypes.enrollment,
      values: [{ text: Strings.all, value: 'all' }]
        .concat(Object.values(KPIS.enrollment.kpis).map(k => ({ text: Strings.capPatient.enrollmentAlerts[k], value: k }))),
    },
    archetype: {
      group: 'archetype',
      text: Strings.archetype,
      type: 'radio',
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.patient, value: 'patient' },
        { text: Strings.doctor, value: 'doctor' },
        { text: Strings.superuser, value: 'superuser' },
        { text: Strings.organizationManager, value: 'organization_manager' },
        { text: Strings.capManager, value: 'capmanager' },
        { text: Strings.shipper, value: 'shipper' },
        { text: Strings.pendingRole, value: 'pending_role' },
      ],
    },
    archetypeExtended: {
      group: 'archetypeExtended',
      text: Strings.archetype,
      type: 'radio',
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.bot, value: 'bot' },
        { text: Strings.patient, value: 'patient' },
        { text: Strings.doctor, value: 'doctor' },
        { text: Strings.superuser, value: 'superuser' },
        { text: Strings.organizationManager, value: 'organization_manager' },
        { text: Strings.capManager, value: 'capmanager' },
        { text: Strings.shipper, value: 'shipper' },
        { text: Strings.pendingRole, value: 'pending_role' },
      ],
    },
    user_state: {
      group: 'user_state',
      text: Strings.status,
      type: 'radio',
      propertyForApi: 'is_active',
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.active, value: 'true' },
        { text: Strings.inactive, value: 'false' },
      ],
    },
    order_category: {
      group: 'order_category',
      text: Strings.shipping.orderCategory,
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.shipping.orderCategories.bulk, value: 'bulk' },
        { text: Strings.shipping.orderCategories.direct, value: 'direct' },
      ],
    },
    flow: {
      group: 'flow',
      text: Strings.shipping.shippingFlow,
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.shipping.shippingFlows.outbound, value: 'outbound' },
        { text: Strings.shipping.shippingFlows.return, value: 'return' },
      ],
    },
    orderInProgressSubStatuses: {
      group: 'orderInProgressSubStatuses',
      text: Strings.shipping.orderInProgressSubStatuses,
      values: [
        { text: Strings.all, value: 'all' },
        { text: Strings.shipping.orderStatuses.open, value: OrderStatusEnum.open },
        { text: Strings.shipping.orderStatuses.in_fulfillment, value: OrderStatusEnum.in_fulfillment },
        { text: Strings.shipping.orderStatuses.partially_delivered, value: OrderStatusEnum.partially_delivered },
      ],
    },
    fulfillment_provider: {
      group: 'fulfillment_provider',
      text: Strings.shipping.fulfillmentProvider,
      values: [
        { text: Strings.all, value: 'all' },
        { text: "SmartMeter", value: 'smartmeter' },
        { text: "RxCap", value: 'rxcap' },
      ],
    },
    missingMedicationSchedule: {
      group: 'missingMedicationSchedule',
      text: Strings.capPatient.noMedicationSchedule,
      tooltip: Strings.capPatient.noMedicationScheduleFilterTooltip,
      type: 'radio',
      values: [
        { text: Strings.noFilter, value: VALUE_FOR_NO_FILTER },
        { text: Strings.yes, value: 'true' },
      ],
    },
    includeInactivePatients: {
      group: 'includeInactivePatients',
      text: Strings.shipping.showInactivePatients,
      tooltip: Strings.shipping.inactivePatientsTooltip,
      type: 'radio',
      values: [
        { text: Strings.noFilter, value: VALUE_FOR_NO_FILTER },
        { text: Strings.yes, value: 'true' },
      ],
    },
    timelineEventType: {
      group: 'timelineEventType',
      text: Strings.eventType,
      propertyForApi: 'event_type',
      values: [
        { text: Strings.all, value: 'all' },
        {value: 'kit_added', text: Strings.timelineEventTypesEnum.kit_added, groupedOptionName: Strings.kit},
        {value: 'kit_removed', text: Strings.timelineEventTypesEnum.kit_removed, groupedOptionName: Strings.kit},
        {value: 'org_assigned', text: Strings.timelineEventTypesEnum.org_assigned, groupedOptionName: Strings.organization},
        {value: 'org_unassigned', text: Strings.timelineEventTypesEnum.org_unassigned, groupedOptionName: Strings.organization},
        {value: 'shipment_new', text: Strings.timelineEventTypesEnum.shipment_new, groupedOptionName: Strings.shipping.shipping},
        {value: 'shipment_created', text: Strings.timelineEventTypesEnum.shipment_created, groupedOptionName: Strings.shipping.shipping},
        {value: 'shipment_in_transit', text: Strings.timelineEventTypesEnum.shipment_in_transit, groupedOptionName: Strings.shipping.shipping},
        {value: 'shipment_delivered', text: Strings.timelineEventTypesEnum.shipment_delivered, groupedOptionName: Strings.shipping.shipping},
        {value: 'shipment_cancelled', text: Strings.timelineEventTypesEnum.shipment_cancelled, groupedOptionName: Strings.shipping.shipping},
        { value: 'status_changed', text: Strings.timelineEventTypesEnum.status_changed },
        {value: 'billing_record_created', text: Strings.timelineEventTypesEnum.billing_record_created },
      ],
    },
    incidentStatus: {
      group: 'incidentStatus',
      text: 'Incident Status',
      type: 'radio',
      values: [
        { text: 'All', value: 'all' },
        { text: 'Open', value: 'open' },
        { text: 'Resolved', value: 'resolved' },
      ],
    },
  };
};


export const OrgFilter = (orgs, includeNone = false, id = 'orgId', text = Strings.organization) => {
  const ret = {
    [id]: {
      group: id,
      text,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
      ]
    },
  };

  if (includeNone) {
    ret[id].values.push({ text: Strings.none, value: '' });
  }

  ret[id].values = ret[id].values.concat(
    getOrganizationHierarchyRows(orgs).map(organization => {
      return {
        text: organization.name,
        value: organization.masked_id,
        hierarchyDepth: organization.hierarchyOptionDepth,
        hierarchyBranchDepth: organization.hierarchyBranchDepth,
        hierarchyBranchValues: organization.hasHierarchyChildren
          ? organization.hierarchyBranchIds
          : undefined,
        hierarchyAncestorValues: organization.hierarchyAncestorIds,
      };
    })
  );
  return ret;
};

export const OrgGroupFilter = groups => {
  const ret = {
    orgGroup: {
      group: 'orgGroup',
      text: Strings.organizationGroup,
      type: 'radio',
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
      ]
    },
  };

  ret.orgGroup.values = ret.orgGroup.values.concat(
    groups.map(f => {
      return { text: f.name, value: f.id };
    })
  );
  return ret;
};

export const CareProviderFilter = careProviders => {
  return {
    provider: {
      group: 'provider',
      type: 'radio',
      text: Strings.capPatient.careProvider,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
      ].concat(careProviders.map(f => {
        return { text: f.name, value: f.uuid };
      }))
    }
  };
};

export const OrgTypeFilter = orgTypes => {
  return {
    orgType: {
      group: 'orgType',
      // type: 'radio',
      text: Strings.organizationType,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
      ].concat(orgTypes.map(f => {
        return { text: f, value: f };
      }))
    }
  };
};

export const dateFilter = dateFilters => {
  return {
    dateFilter: {
      group: 'dateFilter',
      text: Strings.dateRange,
      type: 'radio',
      values: [{ text: Strings.noFilter, value: VALUE_FOR_NO_FILTER }].concat(Object.values(dateFilters).map(v => ({
        text: v.label,
        value: v.value,
      }))),
    }
  };
};

export const ManufactuterFilter = manufacturers => {
  return {
    manufacturer: {
      group: 'manufacturer',
      text: Strings.manufacturer,
      values: [
        {
          text: Strings.all,
          value: 'all',
        },
      ].concat(manufacturers.map(m => ({ text: m, value: m })))
    }
  };
};

export const MonthFilter = (text, datePickerProps) => {
  return {
    month: {
      group: 'month',
      text: text || Strings.capPatient.month,
      type: 'date',
      datePickerProps,
      format: "YYYY-MM",
    },
  };
};
